import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useApp } from "../contexts/AppContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import axios from "axios";
import {
  Users, Building2, MapPin, Clock, Calendar,
  TrendingUp, Briefcase, Database, ChevronLeft, ChevronRight, Ghost, Wallet
} from "lucide-react";
import { MONTH_NAMES, formatCurrency, API_URL } from "../lib/utils";


const MONTH_SHORT = ["", "Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const BAR_COLORS = ["#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#6366F1", "#14B8A6", "#F97316", "#84CC16"];

function BarChart({ data, labels, title, colors, height = 220 }) {
  const maxVal = Math.max(...data.map(d => d || 0), 1);
  return (
    <div data-testid="bar-chart">
      <h3 className="font-ibm-plex font-semibold text-sm text-[#52525B] mb-3">{title}</h3>
      <div className="flex items-end gap-1.5" style={{ height }}>
        {data.map((val, i) => {
          const h = Math.max((val / maxVal) * (height - 30), 2);
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1">
              <span className="text-[9px] font-ibm-plex-mono text-[#52525B] truncate max-w-full">
                {val > 0 ? formatCurrency(val) : ""}
              </span>
              <div
                className="w-full rounded-t-sm transition-all duration-300"
                style={{ height: h, backgroundColor: colors?.[i] || BAR_COLORS[i % BAR_COLORS.length], minWidth: 12 }}
                title={`${labels[i]}: ${formatCurrency(val)}`}
              />
              <span className="text-[10px] font-ibm-plex text-[#71717A] truncate max-w-full">{labels[i]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StackedBar({ regular, ghost, labels, title, height = 220 }) {
  const maxVal = Math.max(...regular.map((r, i) => (r || 0) + (ghost[i] || 0)), 1);
  return (
    <div data-testid="stacked-bar-chart">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-ibm-plex font-semibold text-sm text-[#52525B]">{title}</h3>
        <div className="flex gap-3 text-[10px] font-ibm-plex">
          <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-[#0EA5E9]" /> Stipendi</div>
          <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-[#71717A]" /> Indennità</div>
        </div>
      </div>
      <div className="flex items-end gap-1.5" style={{ height }}>
        {regular.map((regVal, i) => {
          const ghostVal = ghost[i] || 0;
          const total = regVal + ghostVal;
          const hReg = Math.max((regVal / maxVal) * (height - 30), total > 0 ? 2 : 0);
          const hGhost = Math.max((ghostVal / maxVal) * (height - 30), ghostVal > 0 ? 2 : 0);
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1">
              <span className="text-[9px] font-ibm-plex-mono text-[#52525B] truncate max-w-full">
                {total > 0 ? formatCurrency(total) : ""}
              </span>
              <div className="w-full flex flex-col justify-end" style={{ minWidth: 12 }}>
                {ghostVal > 0 && (
                  <div className="w-full rounded-t-sm bg-[#71717A]" style={{ height: hGhost }} title={`Indennità: ${formatCurrency(ghostVal)}`} />
                )}
                {regVal > 0 && (
                  <div className={`w-full bg-[#0EA5E9] ${ghostVal === 0 ? "rounded-t-sm" : ""}`} style={{ height: hReg }} title={`Stipendi: ${formatCurrency(regVal)}`} />
                )}
              </div>
              <span className="text-[10px] font-ibm-plex text-[#71717A]">{labels[i]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, isAdmin } = useAuth();
  const { fetchCompanies, fetchLocations, fetchEmployees, seedData, loading, employees } = useApp();
  const [stats, setStats] = useState(null);
  const [payrollStats, setPayrollStats] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [payrollYear, setPayrollYear] = useState(new Date().getFullYear());
  const [viewMode, setViewMode] = useState("global");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [timbratureStats, setTimbratureStats] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
    fetchCompanies();
    fetchLocations();
    fetchEmployees();
  }, [fetchCompanies, fetchLocations, fetchEmployees]);

  useEffect(() => {
    fetchPayrollStats();
  }, [payrollYear]);

  useEffect(() => {
    fetchTimbratureStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stats/dashboard`, { withCredentials: true });
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const fetchPayrollStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stats/payroll-summary`, {
        params: { year: payrollYear }, withCredentials: true
      });
      setPayrollStats(response.data);
    } catch (err) {
      console.error("Error fetching payroll stats:", err);
    }
  };

  const fetchTimbratureStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stats/timbrature`, { withCredentials: true });
      setTimbratureStats(response.data);
    } catch (err) {
      console.error("Error fetching timbrature:", err);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const result = await seedData();
      alert(`Seed completato! ${result.employees_created} dipendenti creati.`);
      fetchDashboardStats();
    } catch (err) {
      alert("Errore durante il seed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSeeding(false);
    }
  };

  const now = new Date();
  const currentMonth = MONTH_NAMES[now.getMonth() + 1];
  const currentYear = now.getFullYear();

  // Compute chart data
  const months12 = Array.from({ length: 12 }, (_, i) => i + 1);

  const globalMonthly = useMemo(() => {
    if (!payrollStats) return months12.map(() => 0);
    return months12.map(m => payrollStats.global?.[m] || 0);
  }, [payrollStats]);

  const ghostRegularData = useMemo(() => {
    if (!payrollStats) return { regular: months12.map(() => 0), ghost: months12.map(() => 0) };
    return {
      regular: months12.map(m => payrollStats.ghost_split?.regular?.[m] || 0),
      ghost: months12.map(m => payrollStats.ghost_split?.ghost?.[m] || 0),
    };
  }, [payrollStats]);

  const companyList = useMemo(() => {
    if (!payrollStats?.by_company) return [];
    return Object.entries(payrollStats.by_company).map(([id, data]) => ({
      id, name: data.name, months: data.months
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [payrollStats]);

  const locationList = useMemo(() => {
    if (!payrollStats?.by_location) return [];
    return Object.entries(payrollStats.by_location).map(([id, data]) => ({
      id, name: data.name, company_id: data.company_id, company_name: data.company_name, months: data.months
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [payrollStats]);

  const filteredLocations = useMemo(() => {
    if (!selectedCompany) return locationList;
    return locationList.filter(l => l.company_id === selectedCompany);
  }, [locationList, selectedCompany]);

  const yearTotal = globalMonthly.reduce((s, v) => s + v, 0);
  const ghostTotal = ghostRegularData.ghost.reduce((s, v) => s + v, 0);
  const regularTotal = ghostRegularData.regular.reduce((s, v) => s + v, 0);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-outfit tracking-tight text-[#09090B]">
            Benvenuto, {user?.name || "Utente"}
          </h1>
          <p className="text-[#52525B] font-ibm-plex mt-1">
            {currentMonth} {currentYear} - Panoramica del gestionale
          </p>
        </div>
        {isAdmin && stats?.total_employees === 0 && (
          <Button data-testid="seed-data-button" onClick={handleSeedData} disabled={seeding}
            className="bg-black hover:bg-gray-800 text-white font-ibm-plex">
            <Database className="w-4 h-4 mr-2" />
            {seeding ? "Caricamento..." : "Carica Dati Demo"}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Dipendenti", value: stats?.total_employees || 0, sub: "Totale attivi", icon: Users, color: "#0EA5E9" },
          { label: "Aziende", value: stats?.total_companies || 0, sub: "Società gestite", icon: Building2, color: "#10B981" },
          { label: "Unità Locali", value: stats?.total_locations || 0, sub: "Sedi operative", icon: MapPin, color: "#F59E0B" },
          { label: "Ore Mese", value: Math.ceil(stats?.current_month?.total_hours || 0), sub: `${currentMonth} ${currentYear}`, icon: Clock, color: "#EF4444" },
        ].map(({ label, value, sub, icon: Icon, color }) => (
          <Card key={label} className="border border-[#E4E4E7] shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-[#52525B] font-ibm-plex">{label}</CardTitle>
              <Icon className="h-5 w-5" style={{ color }} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-ibm-plex-mono text-[#09090B]">{value}</div>
              <p className="text-xs text-[#52525B] mt-1 font-ibm-plex">{sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Payroll Section */}
      <Card className="border border-[#E4E4E7] shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-lg font-outfit font-semibold text-[#09090B] flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              Riepilogo Stipendi
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPayrollYear(payrollYear - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="font-ibm-plex-mono font-semibold text-sm w-12 text-center">{payrollYear}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPayrollYear(payrollYear + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <Select value={viewMode} onValueChange={(v) => { setViewMode(v); setSelectedCompany(""); }}>
                <SelectTrigger className="font-ibm-plex text-xs h-8 w-[160px]" data-testid="payroll-view-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Globale</SelectItem>
                  <SelectItem value="company">Per Azienda</SelectItem>
                  <SelectItem value="location">Per Unità Locale</SelectItem>
                  <SelectItem value="ghost">Stipendi vs Indennità</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="p-3 bg-[#F4F4F5] rounded-lg text-center">
              <p className="text-xs text-[#52525B] font-ibm-plex">Totale Anno</p>
              <p className="text-lg font-bold font-ibm-plex-mono text-[#09090B]">{formatCurrency(yearTotal)}</p>
            </div>
            <div className="p-3 bg-[#0EA5E9]/10 rounded-lg text-center">
              <p className="text-xs text-[#52525B] font-ibm-plex">Stipendi</p>
              <p className="text-lg font-bold font-ibm-plex-mono text-[#0EA5E9]">{formatCurrency(regularTotal)}</p>
            </div>
            <div className="p-3 bg-[#71717A]/10 rounded-lg text-center">
              <p className="text-xs text-[#52525B] font-ibm-plex">Indennità</p>
              <p className="text-lg font-bold font-ibm-plex-mono text-[#71717A]">{formatCurrency(ghostTotal)}</p>
            </div>
          </div>

          {/* Charts */}
          {viewMode === "global" && (
            <BarChart
              data={globalMonthly}
              labels={MONTH_SHORT.slice(1)}
              title={`Stipendi Mensili ${payrollYear} — Tutte le aziende`}
              colors={months12.map(() => "#0EA5E9")}
            />
          )}

          {viewMode === "ghost" && (
            <StackedBar
              regular={ghostRegularData.regular}
              ghost={ghostRegularData.ghost}
              labels={MONTH_SHORT.slice(1)}
              title={`Stipendi vs Indennità — ${payrollYear}`}
            />
          )}

          {viewMode === "company" && (
            <div className="space-y-6">
              {companyList.map((comp, idx) => {
                const data = months12.map(m => comp.months?.[m] || 0);
                const compTotal = data.reduce((s, v) => s + v, 0);
                if (compTotal === 0) return null;
                return (
                  <div key={comp.id}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-ibm-plex font-medium text-sm">{comp.name}</span>
                      <span className="font-ibm-plex-mono text-sm font-semibold">{formatCurrency(compTotal)}</span>
                    </div>
                    <BarChart
                      data={data}
                      labels={MONTH_SHORT.slice(1)}
                      title=""
                      colors={months12.map(() => BAR_COLORS[idx % BAR_COLORS.length])}
                      height={140}
                    />
                  </div>
                );
              })}
              {companyList.every(c => months12.every(m => !(c.months?.[m]))) && (
                <p className="text-center text-sm text-[#71717A] font-ibm-plex py-4">Nessun dato per {payrollYear}</p>
              )}
            </div>
          )}

          {viewMode === "location" && (
            <div className="space-y-4">
              <Select value={selectedCompany || "all"} onValueChange={(v) => setSelectedCompany(v === "all" ? "" : v)}>
                <SelectTrigger className="font-ibm-plex text-xs h-8 w-[250px]" data-testid="payroll-location-filter">
                  <SelectValue placeholder="Filtra per azienda" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le aziende</SelectItem>
                  {companyList.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {filteredLocations.map((loc, idx) => {
                const data = months12.map(m => loc.months?.[m] || 0);
                const locTotal = data.reduce((s, v) => s + v, 0);
                if (locTotal === 0) return null;
                return (
                  <div key={loc.id}>
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <span className="font-ibm-plex font-medium text-sm">{loc.name}</span>
                        <span className="text-[10px] text-[#71717A] font-ibm-plex ml-2">{loc.company_name}</span>
                      </div>
                      <span className="font-ibm-plex-mono text-sm font-semibold">{formatCurrency(locTotal)}</span>
                    </div>
                    <BarChart
                      data={data}
                      labels={MONTH_SHORT.slice(1)}
                      title=""
                      colors={months12.map(() => BAR_COLORS[(idx + 2) % BAR_COLORS.length])}
                      height={120}
                    />
                  </div>
                );
              })}
              {filteredLocations.every(l => months12.every(m => !(l.months?.[m]))) && (
                <p className="text-center text-sm text-[#71717A] font-ibm-plex py-4">Nessun dato per {payrollYear}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Timbrature del mese */}
      {timbratureStats && Object.keys(timbratureStats.employees || {}).length > 0 && (
        <Card className="border border-[#E4E4E7] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-outfit font-semibold text-[#09090B] flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Timbrature {currentMonth} {currentYear}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E4E4E7]">
                    <th className="text-left font-ibm-plex font-semibold py-2 pr-4">Dipendente</th>
                    <th className="text-right font-ibm-plex font-semibold py-2 px-2">Ore Mese</th>
                    <th className="text-right font-ibm-plex font-semibold py-2 pl-2">Giorni</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(timbratureStats.employees).map(([empId, data]) => {
                    const emp = employees.find(e => e.id === empId);
                    if (!emp) return null;
                    const daysCount = Object.keys(data.days).length;
                    return (
                      <tr key={empId} className="border-b border-[#F4F4F5] last:border-0">
                        <td className="font-ibm-plex py-2 pr-4">{emp.last_name} {emp.first_name}</td>
                        <td className="font-ibm-plex-mono text-right py-2 px-2 font-medium">{data.total_hours}h</td>
                        <td className="font-ibm-plex-mono text-right py-2 pl-2">{daysCount}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Stats & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-[#E4E4E7] shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-outfit font-semibold text-[#09090B] flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Statistiche {currentMonth}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-[#0EA5E9]/10 rounded-lg">
                <p className="text-sm text-[#52525B] font-ibm-plex">Giorni Lavorati</p>
                <p className="text-2xl font-bold font-ibm-plex-mono text-[#0EA5E9]">{stats?.current_month?.total_work_days || 0}</p>
              </div>
              <div className="p-4 bg-[#F59E0B]/10 rounded-lg">
                <p className="text-sm text-[#52525B] font-ibm-plex">Giorni Ferie</p>
                <p className="text-2xl font-bold font-ibm-plex-mono text-[#F59E0B]">{stats?.current_month?.total_holiday_days || 0}</p>
              </div>
              <div className="p-4 bg-[#EF4444]/10 rounded-lg">
                <p className="text-sm text-[#52525B] font-ibm-plex">Giorni Malattia</p>
                <p className="text-2xl font-bold font-ibm-plex-mono text-[#EF4444]">{stats?.current_month?.total_sick_days || 0}</p>
              </div>
              <div className="p-4 bg-[#10B981]/10 rounded-lg">
                <p className="text-sm text-[#52525B] font-ibm-plex">Ore Totali</p>
                <p className="text-2xl font-bold font-ibm-plex-mono text-[#10B981]">{stats?.current_month?.total_hours || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-[#E4E4E7] shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-outfit font-semibold text-[#09090B] flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Azioni Rapide
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button variant="outline" className="justify-start font-ibm-plex h-12" onClick={() => window.location.href = "/employees"} data-testid="quick-action-employees">
                <Users className="w-4 h-4 mr-2" /> Gestione Dipendenti
              </Button>
              <Button variant="outline" className="justify-start font-ibm-plex h-12" onClick={() => window.location.href = "/schedules"} data-testid="quick-action-schedules">
                <Calendar className="w-4 h-4 mr-2" /> Orario Mensile
              </Button>
              <Button variant="outline" className="justify-start font-ibm-plex h-12" onClick={() => window.location.href = "/payrolls"} data-testid="quick-action-payrolls">
                <TrendingUp className="w-4 h-4 mr-2" /> Gestione Stipendi
              </Button>
              <Button variant="outline" className="justify-start font-ibm-plex h-12" onClick={() => window.location.href = "/companies"} data-testid="quick-action-companies">
                <Building2 className="w-4 h-4 mr-2" /> Aziende e Sedi
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
